import type { Client } from '@optimizely/optimizely-sdk';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import type { DataLayerEventName, EventData } from '@/app_lib/types/Gtm';
import type { GetAccountSuccess } from '@/app_lib/types/piano/pianoApi';
import type { Tinypass } from '@/app_lib/types/piano/tinypass';

interface TemplateIntersectionProps {
  container: Element;
  optimizely?: Client;
  optimizelyEvent?: string;
  sendEventOnConsent: (eventData: EventData, eventType?: DataLayerEventName) => void;
  threshold?: number;
  trackingPayload: TemplateViewTrackingPayload;
}

const registrationConversionProductId = process.env.NEXT_PUBLIC_PIANO_REGISTRATION_CONVERSION_PRODUCT_ID;

interface RegistrationConversionEventSuccessResult {
  httpStatus: 200;
  response: Record<string, never>;
}

interface RegistrationConversionEventErrorResult {
  httpStatus: number; // The documentation is unclear as to what status codes other than 200 are considered errors
  response: {
    error: string;
  };
}

export type RegistrationConversionEventResult =
  | RegistrationConversionEventSuccessResult
  | RegistrationConversionEventErrorResult;

export interface TemplateViewTrackingPayload {
  displayMode: string;
  experienceActionId: string;
  experienceId: string;
  templateId: string;
  templateVariantId: string | null;
  eventAction: string;
  eventCategory: string;
  eventLabel: string;
}

export async function getExistingPianoUid(email: string): Promise<string | null> {
  const checkAccountResponse = await fetch('/api/checkPianoAccountExists', {
    body: JSON.stringify({ email }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

  if (!checkAccountResponse.ok) {
    if (checkAccountResponse.status === 404) {
      // Successful api call, but no user found
      // Saving space if we wanted to do something different here in the future
      return null;
    }
    // Where we would do something if the Piano API call fails
    // Saving space if we wanted to do something different here in the future
    return null;
  }

  const userData: GetAccountSuccess = await checkAccountResponse.json();

  return userData.getUserResponse.uid;
}

export const getLts = (tp: Tinypass, experienceId: string, eventType: string): string => {
  const experienceResult = tp.experience._getLastExecutionResult();
  const userSegments = experienceResult.result?.events.find(
    (event) => event.eventType === eventType && event.eventExecutionContext.experienceId === experienceId,
  )?.eventExecutionContext.userSegments;
  const lts = userSegments?.COMPOSER1X
    ? userSegments?.COMPOSER1X.segments.find((score) => score.startsWith('LTs'))
    : 'no_score';

  return lts || 'no_score';
};

export const PROMO_CONTAINER_OPTIMIZELY_EVENTS = {
  // TODO: Uncomment other templates once their containers are created
  '#promo-piano-template-recirc-card-container-latest': 'saw_promo_recirc_card',
  '#promo-piano-template-recirc-card-container-most-popular': 'saw_promo_recirc_card',
  '#promo-piano-template-right-rail-container': 'saw_promo_right_rail',
  // '#promo-piano-template-top-bar-container': 'saw_promo_top_bar',
};

export const buildTemplateIntersectionObserver = ({
  container,
  optimizely,
  optimizelyEvent,
  sendEventOnConsent,
  threshold = 0.5,
  trackingPayload,
}: TemplateIntersectionProps) => {
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendEventOnConsent(trackingPayload);
          // Track template exposure to Optimizely if provided
          if (optimizely && optimizelyEvent) {
            trackOptimizelyEvent(optimizely, optimizelyEvent, {});
          }
          obs?.disconnect?.();
        }
      });
    },
    { threshold },
  );
  observer.observe(container);
};

export const sendRegistrationConversionEvent = () => {
  if (process.env.ENVIRONMENT !== 'prod') {
    // biome-ignore lint/suspicious/noConsole: <Keeping console.log for debugging purposes while this is being validated>
    console.log('Registration conversion event intentionally not sent in non-production environment');
    return;
  }

  if (
    typeof window === 'undefined' ||
    typeof window.cX === 'undefined' ||
    typeof window.cX.CCE === 'undefined' ||
    !registrationConversionProductId
  ) {
    // biome-ignore lint/suspicious/noConsole: <Keeping console.error for debugging purposes while this is being validated>
    console.error('CXense conversion tracking is not available');
    return;
  }

  window.cX.CCE.sendConversionEvent(
    { funnelStep: 'convertProduct', productId: registrationConversionProductId },
    {
      callback: (result: RegistrationConversionEventResult) => {
        if (result.httpStatus !== 200) {
          // biome-ignore lint/suspicious/noConsole: <Keeping console.error for debugging purposes while this is being validated>
          console.error(
            'Failed to send registration conversion event',
            (result as RegistrationConversionEventErrorResult).response.error || 'Unknown error',
          );
        }
      },
    },
  );
};
